import {
	FileDocumentType,
	ILinkFilesObjectsVariables,
} from '@app/pages/cmms/interfaces/files.interfaces';
import { IActionCenterLinker } from '../../interfaces';
import { ILocationNode } from '@app/shared/ui-components/au-locations-tree-selector/location-tree-selector.service';
import { FilesService } from '@app/pages/cmms/services/files.service';
import { IClassification } from '@app/pages/cmms/interfaces/classification.interfaces';
import { ICmmsAsset } from '@app/pages/cmms/interfaces/assets.interfaces';
import { firstValueFrom, lastValueFrom, timeout } from 'rxjs';

import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { Apollo } from 'apollo-angular';
import { ILinkLabelsVariables } from './linking-interfaces';
import { linkLabelsActionCenter } from './mutations';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';

export class FileLocationLinker implements IActionCenterLinker<ILocationNode> {
	private readonly filesService: FilesService;

	constructor(deps: { filesService: FilesService }) {
		this.filesService = deps.filesService;
	}

	link(selectedPks: string[], targets: ILocationNode[]): Promise<any> {
		const payload: ILinkFilesObjectsVariables = {
			files: selectedPks,
			locations: targets.map(location => location.locationId),
		};

		const obs = this.filesService.linkFilesObjects(payload);
		return lastValueFrom(obs);
	}
}

export class FileDocTypeLinker implements IActionCenterLinker<FileDocumentType> {
	private readonly filesService: FilesService;

	constructor(deps: { filesService: FilesService }) {
		this.filesService = deps.filesService;
	}

	link(selectedPks: string[], targets: FileDocumentType[]): Promise<any> {
		const obs = this.filesService.updateFilesType(selectedPks, targets[0]);
		return lastValueFrom(obs);
	}
}

export class FileClassificationLinker implements IActionCenterLinker<IClassification> {
	private readonly filesService: FilesService;

	constructor(deps: { filesService: FilesService }) {
		this.filesService = deps.filesService;
	}

	link(selectedPks: string[], targets: IClassification[]): Promise<any> {
		const payload: ILinkFilesObjectsVariables = {
			files: selectedPks,
			classifications: targets.map(classification => classification.pk),
		};
		const obs = this.filesService.linkFilesObjects(payload);
		return lastValueFrom(obs);
	}
}

export class FileAssetsLinker implements IActionCenterLinker<ICmmsAsset> {
	private readonly filesService: FilesService;

	constructor(deps: { filesService: FilesService }) {
		this.filesService = deps.filesService;
	}

	link(selectedPks: string[], targets: ICmmsAsset[]): Promise<any> {
		const payload: ILinkFilesObjectsVariables = {
			files: selectedPks,
			assets:
				targets.map(asset => {
					return { name: asset.name, id: asset.pk };
				}) || [],
		};
		const obs = this.filesService.linkFilesObjects(payload);
		return lastValueFrom(obs);
	}
}

export class FileWorkOrdersLinker implements IActionCenterLinker<IWorkOrder> {
	private readonly filesService: FilesService;

	constructor(deps: { filesService: FilesService }) {
		this.filesService = deps.filesService;
	}

	link(selectedPks: string[], targets: IWorkOrder[]): Promise<any> {
		const payload: ILinkFilesObjectsVariables = {
			files: selectedPks,
			workOrders: targets.map(item => item.pk),
		};
		const obs = this.filesService.linkFilesObjects(payload);
		return lastValueFrom(obs);
	}
}

export class FileIncidentLinker implements IActionCenterLinker<IIncident> {
	private readonly filesService: FilesService;

	constructor(deps: { filesService: FilesService }) {
		this.filesService = deps.filesService;
	}

	link(selectedPks: string[], targets: IIncident[]): Promise<any> {
		const payload: ILinkFilesObjectsVariables = {
			files: selectedPks,
			incidents: targets.map(item => item.pk),
		};
		const obs = this.filesService.linkFilesObjects(payload);
		return lastValueFrom(obs);
	}
}

export class FileRequestLinker implements IActionCenterLinker<IRequest> {
	private readonly filesService: FilesService;

	constructor(deps: { filesService: FilesService }) {
		this.filesService = deps.filesService;
	}

	link(selectedPks: string[], targets: IRequest[]): Promise<any> {
		const payload: ILinkFilesObjectsVariables = {
			files: selectedPks,
			requests: targets.map(item => item.pk),
		};
		const obs = this.filesService.linkFilesObjects(payload);
		return lastValueFrom(obs);
	}
}

export class FileLabelLinker implements IActionCenterLinker<string> {
	private readonly apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: string[]): Promise<any> {
		const variables: ILinkLabelsVariables = {
			fileIds: selectedPks,
			labelIds: targets,
		};
		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkLabelsActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}
